import React, { useMemo } from 'react';

import styled from 'styled-components';

import ContentContainer from 'components/ContentContainer/ContentContainer';
import ForexTable, { ForexTableColumns } from 'components/ForexTable/ForexTable';
import RichTextField from 'components/RichTextField/RichTextField';

import { LivePricingBlockI, LivePricingBlockVariant } from 'interfaces/content-stack/modular-blocks/livePricingBlock';

import { pricingVariantTabs } from 'utils/forexTableData';

export interface LivePricingBlockProps {
  data: LivePricingBlockI;
  className?: string;
}

const columnSetup: { [k in LivePricingBlockVariant]: ForexTableColumns } = {
  home: {
    currency: 'Markets',
    BID: 'Sell',
    OFR: 'Buy',
    CPT: 'Change',
    SPRD: false,
    SWAP_5_LONG: false,
    SWAP_5_SHORT: false,
  },
  dashboard: {
    currency: 'Markets',
    BID: 'Sell',
    OFR: 'Buy',
    CPT: 'Change',
    SPRD: false,
    SWAP_5_LONG: false,
    SWAP_5_SHORT: false,
  },
  rates: {
    currency: 'Markets',
    BID: 'Bid',
    OFR: 'Offer',
    CPT: 'Change',
    SPRD: false,
    SWAP_5_LONG: false,
    SWAP_5_SHORT: false,
  },
  overnight: {
    currency: 'Markets',
    BID: 'Sell',
    OFR: 'Buy',
    CPT: 'Change',
    SPRD: false,
    SWAP_5_LONG: 'Swap Offer',
    SWAP_5_SHORT: 'Swap Bid',
  },
  'overnight-spread': {
    currency: 'Markets',
    BID: 'Sell',
    OFR: 'Buy',
    CPT: false,
    SPRD: 'Spread',
    SWAP_5_LONG: false,
    SWAP_5_SHORT: false,
  },
};

const LivePricingHeader = styled.h3`
  text-transform: uppercase;
  text-align: left;
  margin-bottom: 1rem;
  font-size: 2rem;
  font-family: ${(props) => props.theme.fonts.fontFamilySecondary};
`;

const LivePricingSubheader = styled(RichTextField)`
  font-size: 1.25rem;
  font-family: ${(props) => props.theme.fonts.default};
  font-weight: 500;
  line-height: 2rem;

  & p {
    font-size: 1.25rem;
    font-family: ${(props) => props.theme.fonts.default};
    font-weight: 500;
    line-height: 2rem;
  }
`;

const LivePricingBlock: React.FC<LivePricingBlockProps> = ({
  data: { header, subheader, variant },
  className,
}: LivePricingBlockProps) => {
  const tabs = useMemo(() => {
    return pricingVariantTabs[variant] ?? pricingVariantTabs.home;
  }, [variant]);

  const columns = useMemo(() => columnSetup[variant] ?? columnSetup.home, [variant]);

  return (
    <ContentContainer className={className} zIndex={'10'}>
      {header && <LivePricingHeader>{header}</LivePricingHeader>}
      {subheader && <LivePricingSubheader richText={subheader} />}
      <ForexTable tabs={tabs} columns={columns} />
    </ContentContainer>
  );
};

export default React.memo(LivePricingBlock);
